import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { ArrowLeft, Building2, MapPin, Mail, Phone, FileText, Upload, CheckCircle2, User } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TGA_LOGO = "https://customer-assets.emergentagent.com/job_853d16bb-77c9-4e1d-85ff-db52f6af924e/artifacts/lsv7codb_Copy%20of%20TGA%20pro%20tour%20logo%20transparent.png";

export const VenueRegisterPage = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: '', contact_name: '', address: '', email: '', phone: '', description: '' });
  const [logoUrl, setLogoUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API_URL}/venues/upload-logo`, formData);
      setLogoUrl(res.data.image_url);
      toast.success('Logo uploaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.contact_name || !form.address || !form.email) {
      toast.error('Please fill in venue name, your name, address, and email');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/venues/register`, { ...form, logo_url: logoUrl });
      setSuccess(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Registration failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen hero-bg flex flex-col items-center justify-center px-6">
        <Card className="bg-card border-white/10 w-full max-w-md">
          <CardContent className="p-8 text-center">
            <CheckCircle2 className="w-16 h-16 text-secondary mx-auto mb-4" />
            <h2 className="text-xl font-heading text-white mb-2">Registration Submitted!</h2>
            <p className="text-muted-foreground mb-6 text-sm">
              Your venue application has been submitted. You'll receive an email with login credentials once a Super Admin approves your venue.
            </p>
            <Button onClick={() => navigate('/')} className="bg-primary hover:bg-primary/90 rounded-pill px-8" data-testid="back-to-home-btn">
              Back to Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen hero-bg flex flex-col">
      <div className="p-6 flex items-center justify-between">
        <Button variant="ghost" onClick={() => navigate('/')} className="text-muted-foreground hover:text-white" data-testid="back-btn">
          <ArrowLeft className="mr-2 h-4 w-4" /> Back
        </Button>
        <img src={TGA_LOGO} alt="TGA" className="h-10 object-contain" />
        <div className="w-20" />
      </div>

      <div className="flex-1 flex items-start justify-center px-6 pb-20 pt-4">
        <div className="w-full max-w-lg animate-fade-in">
          <Card className="bg-card border-white/10">
            <CardHeader>
              <CardTitle className="font-heading text-2xl flex items-center gap-2">
                <Building2 className="w-6 h-6 text-primary" /> Register Your Venue
              </CardTitle>
              <CardDescription>Submit your venue for TGA Pro Tour approval. Once approved, you'll receive login credentials to create events.</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label>Venue Name *</Label>
                  <div className="relative">
                    <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input placeholder="e.g. Sunrise Golf Club" value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="pl-10 bg-muted border-border" required data-testid="venue-name-input" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Your Name *</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input placeholder="Your full name" value={form.contact_name} onChange={e => setForm({...form, contact_name: e.target.value})} className="pl-10 bg-muted border-border" required data-testid="venue-contact-name-input" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Address *</Label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input placeholder="Full address" value={form.address} onChange={e => setForm({...form, address: e.target.value})} className="pl-10 bg-muted border-border" required data-testid="venue-address-input" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Contact Email *</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input type="email" placeholder="venue@example.com" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="pl-10 bg-muted border-border" required data-testid="venue-email-input" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Phone</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input placeholder="(555) 123-4567" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} className="pl-10 bg-muted border-border" data-testid="venue-phone-input" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Description</Label>
                  <div className="relative">
                    <FileText className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <textarea
                      placeholder="Tell us about your venue..."
                      value={form.description}
                      onChange={e => setForm({...form, description: e.target.value})}
                      className="flex w-full rounded-md border border-border bg-muted px-3 py-2 pl-10 text-sm text-white placeholder:text-muted-foreground min-h-[80px] focus:border-primary focus:outline-none"
                      data-testid="venue-description-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Venue Logo</Label>
                  <div className="flex items-center gap-3">
                    {logoUrl ? (
                      <img src={logoUrl} alt="Logo" className="w-16 h-16 rounded-lg object-cover border border-white/10" />
                    ) : (
                      <div className="w-16 h-16 rounded-lg bg-muted border border-dashed border-white/20 flex items-center justify-center">
                        <Upload className="w-5 h-5 text-muted-foreground" />
                      </div>
                    )}
                    <label className="cursor-pointer">
                      <input type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" data-testid="venue-logo-upload" />
                      <span className="text-sm text-primary hover:underline">{uploading ? 'Uploading...' : logoUrl ? 'Change logo' : 'Upload logo'}</span>
                    </label>
                  </div>
                </div>
                <Button type="submit" className="w-full bg-primary hover:bg-primary/90 rounded-pill btn-hover-scale mt-4" disabled={submitting} data-testid="venue-register-submit">
                  {submitting ? 'Submitting...' : 'Submit for Approval'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};
